/**
 * frontend/src/pages/valuation/_SnapshotHistory.tsx
 *
 * VALUATION-B3 sign-off — the version history, and the reproducibility check.
 *
 * Two things a signed valuation has to be able to answer, and neither of them
 * is "what is it worth":
 *
 *   * **What did we say before?** Re-finalize does not overwrite. It writes a
 *     new version and marks the prior one superseded, and the prior one stays.
 *     An engagement whose history of opinions quietly disappeared would be a
 *     worse record than one that never had versions at all, so the superseded
 *     rows render here alongside the live one rather than being filtered out.
 *   * **Does it still reproduce?** `GET .../snapshot` replays the frozen inputs
 *     through the frozen config and compares field by field. A record that no
 *     longer reproduces is the single most important thing this screen can say,
 *     so it is rendered loudly and it is never hidden behind a toggle.
 *
 * Split out of ValuationSignOff.tsx under docs/FILE_SIZE_POLICY.md.
 *
 * VAL-VERSION-DIFF-1: each row that supersedes an earlier one gets a
 * collapsed "What changed" toggle, fetched only on open via the existing
 * `fetchSnapshot` — reads `valuationId` off the URL itself (the same param
 * ValuationShell.tsx reads) rather than growing this component's props,
 * since the caller (ValuationSignOff.tsx) is off limits for this brief.
 *
 * VAL-HISTORY-4a: `showDocuments` (default false, used by the History
 * table's per-engagement "Versions" expansion) fetches this engagement's
 * document archive once and renders each version's newest .pdf/.docx via
 * the shared `IssuedDocumentList` — no second download function. A failed
 * fetch renders a distinct error message, not "no documents" — the two are
 * not the same claim and must not collapse into one code path.
 */
import { Fragment, useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'
import { Card, CardContent, CardHeader } from '@/components/ui/card'
import { CollapsibleTitle, useCollapsible } from './_CollapsibleCard'
import { fetchSnapshot } from '@/api/valuationB3'
import { fetchValuationDocuments, type ValuationDocumentRow } from '@/api/valuation'
import type { SnapshotHistoryRow, SnapshotVerification } from '@/types/valuationB3'
import { money, multiple } from '@/utils/format'
import { formatDateTime } from '@/utils/date'
import { diffSnapshots, type SnapshotDiffRow } from './_snapshotDiff'
import { IssuedDocumentList } from './_OverviewDocuments'
import { formatApiError } from '@/utils/httpErrors'

/** Newest .pdf and newest .docx (by produced_at) issued for one version, if any. */
function docsForVersion(documents: ValuationDocumentRow[], version: number): ValuationDocumentRow[] {
  const forVersion = documents.filter(d => d.snapshot_version === version)
  const newestByExt = (ext: string) =>
    forVersion
      .filter(d => d.filename.toLowerCase().endsWith(`.${ext}`))
      .sort((a, b) => b.produced_at.localeCompare(a.produced_at))[0]
  return [newestByExt('pdf'), newestByExt('docx')].filter(
    (d): d is ValuationDocumentRow => d !== undefined,
  )
}

/** Per-version document list, or the "no document" fallback. Collapsed under each row. */
function VersionDocuments({
  engagementId,
  version,
  documents,
  error,
}: {
  engagementId: string
  version: number
  documents: ValuationDocumentRow[] | null
  error: string | null
}) {
  if (error) return <p className="mt-2 text-xs text-red-700">{error}</p>
  if (documents === null) return <p className="mt-2 text-xs text-gray-500">Loading documents…</p>
  const rows = docsForVersion(documents, version)
  return rows.length === 0 ? (
    <p className="mt-2 text-xs text-gray-500">No document issued for this version.</p>
  ) : (
    <div className="mt-2">
      <IssuedDocumentList engagementId={engagementId} documents={rows} />
    </div>
  )
}

// ── Reproducibility ──────────────────────────────────────────────────────────

export function VerificationPanel({ verification }: { verification: SnapshotVerification }) {
  const ok = verification.reproduced
  return (
    <div
      className={`rounded-lg border p-4 ${
        ok
          ? 'border-green-200 bg-green-50 text-green-900'
          : 'border-red-300 bg-red-50 text-red-900'
      }`}
    >
      <p className="text-sm font-semibold">
        {ok ? '✓ Record reproduces' : '! Record no longer reproduces'}
      </p>
      <p className="mt-1 text-xs opacity-90">{verification.summary}</p>
      {verification.differences.length > 0 && (
        <ul className="mt-2 list-disc space-y-1 pl-5 text-xs">
          {verification.differences.map((d, i) => <li key={i}>{d}</li>)}
        </ul>
      )}
      {!ok && (
        <p className="mt-2 text-xs font-medium">
          Do not produce a document from this version. Re-finalize to create a version that
          reproduces; this one is retained for the audit trail.
        </p>
      )}
    </div>
  )
}

// ── History ──────────────────────────────────────────────────────────────────

function StatusPill({ status }: { status: SnapshotHistoryRow['status'] }) {
  return status === 'final'
    ? <span className="rounded bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800">Live</span>
    : <span className="rounded bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-600">Superseded</span>
}

/** Collapsed by default; fetches both frozen versions only once opened. */
function DiffToggle({
  engagementId,
  version,
  supersedes,
}: {
  engagementId: string
  version: number
  supersedes: number
}) {
  const [open, setOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [failed, setFailed] = useState(false)
  const [diff, setDiff] = useState<SnapshotDiffRow[] | null>(null)

  function handleToggle() {
    if (open) {
      setOpen(false)
      return
    }
    setOpen(true)
    if (diff !== null || loading) return
    setLoading(true)
    setFailed(false)
    Promise.all([fetchSnapshot(engagementId, version), fetchSnapshot(engagementId, supersedes)])
      .then(([nextRes, prevRes]) => {
        if (!nextRes.snapshot || !prevRes.snapshot) {
          setFailed(true)
          return
        }
        setDiff(diffSnapshots(prevRes.snapshot, nextRes.snapshot))
      })
      .catch(() => setFailed(true))
      .finally(() => setLoading(false))
  }

  return (
    <div className="mt-2">
      <button
        type="button"
        onClick={handleToggle}
        className="text-xs font-medium text-blue-700 underline decoration-dotted"
      >
        What changed from v{supersedes}
      </button>
      {open && (
        <div className="mt-1 rounded border border-gray-200 bg-gray-50 p-2 text-xs">
          {loading && <p className="text-gray-500">Loading…</p>}
          {!loading && failed && <p className="text-red-700">Could not load this comparison.</p>}
          {!loading && !failed && diff && diff.length === 0 && (
            <p className="text-gray-500">No input changes.</p>
          )}
          {!loading && !failed && diff && diff.length > 0 && (
            <ul className="space-y-1">
              {diff.map(d => (
                <li key={d.field}>
                  <span className="font-medium">{d.label}:</span> {d.before} → {d.after}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  )
}

export function SnapshotHistory({
  rows,
  engagementId,
  showDocuments = false,
  collapsible = false,
}: {
  collapsible?: boolean
  rows: SnapshotHistoryRow[]
  engagementId?: string
  showDocuments?: boolean
}) {
  const { valuationId: routeValuationId } = useParams<{ valuationId: string }>()
  const valuationId = engagementId ?? routeValuationId
  const [documents, setDocuments] = useState<ValuationDocumentRow[] | null>(null)
  const [documentsError, setDocumentsError] = useState<string | null>(null)
  const c = useCollapsible(collapsible)

  useEffect(() => {
    if (!showDocuments || !valuationId) return
    let live = true
    fetchValuationDocuments(valuationId)
      .then(res => { if (live) setDocuments(res.documents) })
      .catch((err: unknown) => { if (live) setDocumentsError(formatApiError(err)) })
    return () => { live = false }
  }, [showDocuments, valuationId])

  return (
    <Card {...c.cardProps}>
      <CardHeader>
        <CollapsibleTitle collapsible={collapsible} open={c.open} titleId={c.titleId} onToggle={c.toggle}>Version history</CollapsibleTitle>
      </CardHeader>
      {c.open && <CardContent>
        {rows.length === 0 ? (
          <p className="text-sm text-gray-500">
            No signed versions yet. The first Finalize creates v1.
          </p>
        ) : (
          <>
            {/* Narrow viewports get stacked cards; a 9-column table at 375px is
                a horizontal scrollbar pretending to be a record. */}
            <ul className="space-y-3 md:hidden">
              {rows.map(row => (
                <li key={row.version} className="rounded-lg border border-gray-200 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-sm font-semibold">v{row.version}</span>
                    <StatusPill status={row.status} />
                  </div>
                  <dl className="mt-2 space-y-1 text-xs text-gray-600">
                    <div className="flex justify-between gap-3">
                      <dt>Signed</dt>
                      <dd className="text-right font-mono">{formatDateTime(row.signed_on)}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt>By</dt>
                      <dd className="text-right">{row.signed_by_name ?? row.signed_by}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt>Multiple</dt>
                      <dd className="text-right font-mono">{multiple(row.concluded_multiple)}</dd>
                    </div>
                    <div className="flex justify-between gap-3">
                      <dt>Band</dt>
                      <dd className="text-right font-mono">
                        {money(row.value_low)} – {money(row.value_high)}
                      </dd>
                    </div>
                  </dl>
                  {row.rationale && (
                    <p className="mt-2 text-xs text-gray-700">Reason: {row.rationale}</p>
                  )}
                  {row.supersedes !== null && valuationId && (
                    <DiffToggle
                      engagementId={valuationId}
                      version={row.version}
                      supersedes={row.supersedes}
                    />
                  )}
                  {showDocuments && valuationId && (
                    <VersionDocuments
                      engagementId={valuationId}
                      version={row.version}
                      documents={documents}
                      error={documentsError}
                    />
                  )}
                </li>
              ))}
            </ul>

            <div className="hidden overflow-x-auto md:block">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase tracking-wide text-gray-500">
                    <th className="py-2 pr-3">Ver</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Signed on</th>
                    <th className="py-2 pr-3">By</th>
                    <th className="py-2 pr-3 text-right">Multiple</th>
                    <th className="py-2 pr-3 text-right">Band</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map(row => (
                    <Fragment key={row.version}>
                      <tr className="border-b last:border-0 align-top">
                        <td className="py-2 pr-3 font-mono">v{row.version}</td>
                        <td className="py-2 pr-3"><StatusPill status={row.status} /></td>
                        <td className="py-2 pr-3 font-mono text-xs">{formatDateTime(row.signed_on)}</td>
                        <td className="py-2 pr-3 text-xs">{row.signed_by_name ?? row.signed_by}</td>
                        <td className="py-2 pr-3 text-right font-mono">
                          {multiple(row.concluded_multiple)}
                        </td>
                        <td className="py-2 pr-3 text-right font-mono text-xs">
                          {money(row.value_low)} – {money(row.value_high)}
                        </td>
                      </tr>
                      {(row.rationale ||
                        (row.supersedes !== null && valuationId) ||
                        (showDocuments && valuationId)) && (
                        <tr className="border-b last:border-0">
                          <td colSpan={6} className="pb-3 pr-3 pt-0">
                            {row.rationale && (
                              <p className="text-xs text-gray-700">Reason: {row.rationale}</p>
                            )}
                            {row.supersedes !== null && valuationId && (
                              <DiffToggle
                                engagementId={valuationId}
                                version={row.version}
                                supersedes={row.supersedes}
                              />
                            )}
                            {showDocuments && valuationId && (
                              <VersionDocuments
                                engagementId={valuationId}
                                version={row.version}
                                documents={documents}
                                error={documentsError}
                              />
                            )}
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            </div>

            <p className="mt-3 text-xs text-gray-500">
              Superseded versions are retained, never deleted. Only the live version may produce
              a document.
            </p>
          </>
        )}
      </CardContent>}
    </Card>
  )
}
